// 🗂 우측 패널 탭 개인 설정 — 순서 변경·숨기기 (2026-08-28 사용자 "멤버/스레드/파일 등 탭을 개인설정에서 순서 변경·숨기기").
//   · 방 탭(멤버/스레드/파일/북마크/메모)·범용 탭(조직도/AI) 모두 순서·숨김 가능 (2026-09-18 사용자 "우측 패널의 ai하고 조직도도 숨김처리").
//   · 🔀 그룹 경계 폐지 (2026-10-15 형 "조직도를 멤버/댓글/파일과 같은 곳에, 맨 앞으로 필요한 인원이 있음") — 한 줄 탭, 순서는 전체에서 자유 이동.
//     기본 순서는 종전과 같다(방 탭 → 조직도 → AI). 패널을 처음 열 때 기본 탭은 순서상 첫 보이는 탭.
//   · 저장 = localStorage `tpt-right-tabs`(user-scoped — sessionPurge 등록). 변경은 RIGHT_TABS_EVENT 로 열린 패널에 즉시 반영.
//   · ⚙️ 계정 단위 동기화 대상(prefsSync, 2026-09-07) — 쓰기 뒤 markPrefChanged. 서버값 반영도 writeRightTabsPref 로 들어온다.
//   · 권한(준회원 파일/조직도/AI 숨김)은 RightPanel 의 capability 판정이 그대로 우선 — 이 설정은 그 결과 위에 얹는 취향값.
import { useEffect, useState } from 'react';
import { markPrefChanged } from './prefs';

export type RightTabId = 'members' | 'thread' | 'files' | 'bookmarks' | 'memo' | 'org' | 'ai';

export const ROOM_TABS_DEFAULT: readonly RightTabId[] = ['members', 'thread', 'files', 'bookmarks', 'memo'];
export const UNIVERSAL_TABS_DEFAULT: readonly RightTabId[] = ['org', 'ai'];
const ALL: readonly RightTabId[] = [...ROOM_TABS_DEFAULT, ...UNIVERSAL_TABS_DEFAULT];
/** 숨길 수 없는 탭 — 2026-09-18 사용자 지시로 조직도·AI 도 숨김 허용되어 현재는 비었다.
 *  (상수 자체는 설정 화면 등 호출부 호환·향후 예외 지정 여지를 위해 남겨 둔다. 비어 있으므로 모든 탭이 숨김 대상) */
export const NON_HIDEABLE: ReadonlySet<RightTabId> = new Set<RightTabId>();

export interface RightTabsPref {
	/** 전체 탭 순서(그룹 무관하게 한 배열) — 그룹별 정렬은 이 배열의 인덱스로 */
	order: RightTabId[];
	hidden: RightTabId[];
}

export const RIGHT_TABS_KEY = 'tpt-right-tabs';
const KEY = RIGHT_TABS_KEY;
export const RIGHT_TABS_EVENT = 'tpt:right-tabs-changed';

const isTab = (v: unknown): v is RightTabId => typeof v === 'string' && (ALL as readonly string[]).includes(v);

/** 저장값을 항상 완전한 형태로 — 모르는 id 제거, 빠진 탭은 기본 순서로 뒤에 붙임, 숨김은 숨길 수 있는 탭만 */
export function normalizeRightTabsPref(raw: Partial<RightTabsPref> | null | undefined): RightTabsPref {
	const seen = new Set<RightTabId>();
	const order: RightTabId[] = [];
	for (const v of raw?.order || []) { if (isTab(v) && !seen.has(v)) { seen.add(v); order.push(v); } }
	for (const v of ALL) { if (!seen.has(v)) { seen.add(v); order.push(v); } }
	const hidden = [...new Set((raw?.hidden || []).filter((v): v is RightTabId => isTab(v) && !NON_HIDEABLE.has(v)))];
	return { order, hidden };
}

export function readRightTabsPref(): RightTabsPref {
	try {
		const s = localStorage.getItem(KEY);
		if (!s) return normalizeRightTabsPref(null);
		return normalizeRightTabsPref(JSON.parse(s) as Partial<RightTabsPref>);
	} catch { return normalizeRightTabsPref(null); }
}

export function writeRightTabsPref(p: RightTabsPref): void {
	const norm = normalizeRightTabsPref(p);
	try { localStorage.setItem(KEY, JSON.stringify(norm)); } catch { /* private mode */ }
	markPrefChanged(KEY);
	try { window.dispatchEvent(new CustomEvent(RIGHT_TABS_EVENT)); } catch { /* */ }
}

export function resetRightTabsPref(): void {
	try { localStorage.removeItem(KEY); } catch { /* */ }
	markPrefChanged(KEY);
	try { window.dispatchEvent(new CustomEvent(RIGHT_TABS_EVENT)); } catch { /* */ }
}

export function isRightTabHidden(p: RightTabsPref, id: string): boolean {
	return !NON_HIDEABLE.has(id as RightTabId) && (p.hidden as string[]).includes(id);
}

/** 전체 순서에서 한 칸 이동 — 방 탭·조직도·AI 사이 경계 없음 (2026-10-15). 양 끝에서는 그대로 */
export function moveRightTab(p: RightTabsPref, id: RightTabId, dir: -1 | 1): RightTabsPref {
	const i = p.order.indexOf(id);
	const j = i + dir;
	if (i < 0 || j < 0 || j >= p.order.length) return p;
	const order = [...p.order]; [order[i], order[j]] = [order[j], order[i]];
	return { ...p, order };
}

/** 패널을 처음 열 때의 탭 — 설정 순서상 숨기지 않은 첫 탭 중 지금 쓸 수 있는 것(available). 없으면 members */
export function firstOpenTab(p: RightTabsPref, available: (id: RightTabId) => boolean): RightTabId {
	return p.order.find((id) => available(id) && !isRightTabHidden(p, id)) ?? 'members';
}

export function setRightTabHidden(p: RightTabsPref, id: RightTabId, hidden: boolean): RightTabsPref {
	if (NON_HIDEABLE.has(id)) return p;
	const set = new Set(p.hidden);
	if (hidden) set.add(id); else set.delete(id);
	return { ...p, hidden: [...set] };
}

/** 탭 배열을 설정 순서로 정렬 (첫 요소가 id 인 튜플 배열) */
export function sortTabsByPref<T extends readonly [string, ...unknown[]]>(tabs: T[], p: RightTabsPref): T[] {
	const idx = new Map<string, number>(p.order.map((t, i) => [t, i] as const));
	return [...tabs].sort((a, b) => (idx.get(a[0]) ?? 99) - (idx.get(b[0]) ?? 99));
}

/** 설정을 구독하는 훅 — 설정 화면에서 바꾸면 열려 있는 패널이 즉시 따라온다 */
export function useRightTabsPref(): RightTabsPref {
	const [pref, setPref] = useState<RightTabsPref>(() => readRightTabsPref());
	useEffect(() => {
		const on = () => setPref(readRightTabsPref());
		window.addEventListener(RIGHT_TABS_EVENT, on);
		window.addEventListener('storage', on);
		return () => { window.removeEventListener(RIGHT_TABS_EVENT, on); window.removeEventListener('storage', on); };
	}, []);
	return pref;
}

/** 🧭 패널 마운트 시 초기 탭 판정 (2026-09-07 원격 "스레드 누르면 AI 창으로 간다").
 *  신호값은 각 요청의 발사 시각(ms). 닫혀 있던 패널이 열리는 순간엔 **가장 최근 신호 하나**만 보고, 그것도 방금(freshMs 안) 발사된
 *  것일 때만 그 탭으로 간다 — 오래된 신호(예전에 한 번 눌렀던 AI)는 무시해 기본 탭(members)으로. null = 신호 없음/전부 오래됨. */
export const SIGNAL_FRESH_MS = 5000;
export function resolveMountTab(signals: { thread?: number; ai?: number; org?: number }, now: number, freshMs = SIGNAL_FRESH_MS): 'thread' | 'ai' | 'org' | null {
	const cands = (['thread', 'ai', 'org'] as const).map((k) => [k, signals[k] || 0] as const).filter(([, v]) => v > 0);
	if (!cands.length) return null;
	const [tab, at] = cands.sort((a, b) => b[1] - a[1])[0];
	return now - at <= freshMs ? tab : null;
}
